import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, IconButton, Paper, Alert, Button, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Switch, FormControlLabel, Chip, CircularProgress, InputAdornment, Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import YouTubeIcon from '@mui/icons-material/YouTube';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { useAdminFetch } from '../hooks/useAdminFetch';
import { YouTubeVideo, YouTubeChannelVideo, VideoCategory, VIDEO_CATEGORY_LABELS } from '../types/YouTubeVideo';

interface TutorialTooltipData {
  id: number;
  section: string;
  step_index: number;
  target: string;
  title: string;
  content: string;
  enabled: boolean;
}

const apiUrl = process.env.REACT_APP_API_URL;

const CATEGORY_COLORS: Record<VideoCategory, 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
  tutorial: 'primary',
  help: 'info',
  feature: 'success',
  update: 'warning',
  general: 'secondary',
};

function formatDuration(iso: string | null | undefined): string {
  if (!iso) return '';
  const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return iso;
  const h = match[1] ? `${match[1]}:` : '';
  const m = match[2] || '0';
  const s = (match[3] || '0').padStart(2, '0');
  return h ? `${h}${m.padStart(2, '0')}:${s}` : `${m}:${s}`;
}

function formatCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

const AdminTutorials: React.FC = () => {
  const navigate = useNavigate();
  const adminFetch = useAdminFetch();

  // Tab state
  const [tab, setTab] = useState(0);

  // Tutorial tooltip state
  const [tooltipSections, setTooltipSections] = useState<Record<string, TutorialTooltipData[]>>({});
  const [sectionLabels, setSectionLabels] = useState<Record<string, string>>({});
  const [selectedSection, setSelectedSection] = useState('');
  const [tooltipLoading, setTooltipLoading] = useState(false);
  const [tooltipSaving, setTooltipSaving] = useState<number | null>(null);
  const [tooltipEdits, setTooltipEdits] = useState<Record<number, Partial<TutorialTooltipData>>>({});
  const [tooltipResetting, setTooltipResetting] = useState(false);

  // Saved videos state
  const [savedVideos, setSavedVideos] = useState<YouTubeVideo[]>([]);
  const [savedLoading, setSavedLoading] = useState(true);
  const [savedTotal, setSavedTotal] = useState(0);
  const [savedPage, setSavedPage] = useState(0);
  const [savedPerPage, setSavedPerPage] = useState(10);
  const [savedSearch, setSavedSearch] = useState('');
  const [savedCategory, setSavedCategory] = useState('');

  // Channel videos state
  const [channelVideos, setChannelVideos] = useState<YouTubeChannelVideo[]>([]);
  const [channelLoading, setChannelLoading] = useState(false);
  const [channelNextPage, setChannelNextPage] = useState<string | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);

  // General state
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addingVideo, setAddingVideo] = useState<YouTubeChannelVideo | null>(null);
  const [addForm, setAddForm] = useState({ category: 'general' as VideoCategory, display_order: 0, active: true, featured: false, admin_notes: '' });
  const [addLoading, setAddLoading] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<YouTubeVideo | null>(null);
  const [editForm, setEditForm] = useState({ category: 'general' as VideoCategory, display_order: 0, active: true, featured: false, admin_notes: '' });
  const [editLoading, setEditLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingVideo, setDeletingVideo] = useState<YouTubeVideo | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewVideoId, setPreviewVideoId] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');

  // Load tutorial tooltips
  const loadTooltips = useCallback(async (preserveSection = false) => {
    setTooltipLoading(true);
    setError(null);
    try {
      const data = await adminFetch(`${apiUrl}/api/admin/tutorials/tooltips/`);
      setTooltipSections(data.sections || {});
      setSectionLabels(data.section_labels || {});
      // Only set initial section on first load, not on refresh
      if (!preserveSection) {
        setSelectedSection((current) => {
          if (!current && Object.keys(data.sections || {}).length > 0) {
            return Object.keys(data.sections)[0];
          }
          return current;
        });
      }
    } catch (err: any) {
      if (!err.message?.includes('Admin authentication')) {
        setError(err.message || 'Failed to load tooltips');
      }
    } finally {
      setTooltipLoading(false);
    }
  }, [adminFetch]);

  // Save a single tooltip
  const handleSaveTooltip = async (tooltip: TutorialTooltipData) => {
    const edits = tooltipEdits[tooltip.id];
    if (!edits) return;
    setTooltipSaving(tooltip.id);
    setError(null);
    try {
      await adminFetch(`${apiUrl}/api/admin/tutorials/tooltips/${tooltip.id}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(edits),
      });
      setSuccessMessage('Tooltip updated');
      setTooltipEdits(prev => {
        const next = { ...prev };
        delete next[tooltip.id];
        return next;
      });
      loadTooltips(true);
    } catch (err: any) {
      setError(err.message || 'Failed to save tooltip');
    } finally {
      setTooltipSaving(null);
    }
  };

  // Toggle tooltip enabled
  const handleToggleTooltipEnabled = async (tooltip: TutorialTooltipData) => {
    setTooltipSaving(tooltip.id);
    try {
      await adminFetch(`${apiUrl}/api/admin/tutorials/tooltips/${tooltip.id}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !tooltip.enabled }),
      });
      loadTooltips(true);
    } catch (err: any) {
      setError(err.message || 'Failed to update tooltip');
    } finally {
      setTooltipSaving(null);
    }
  };

  // Reset section to defaults
  const handleResetSection = async () => {
    if (!selectedSection) return;
    setTooltipResetting(true);
    setError(null);
    try {
      const data = await adminFetch(`${apiUrl}/api/admin/tutorials/tooltips/reset/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section: selectedSection }),
      });
      setSuccessMessage(data.message);
      setTooltipEdits({});
      loadTooltips(true);
    } catch (err: any) {
      setError(err.message || 'Failed to reset tooltips');
    } finally {
      setTooltipResetting(false);
    }
  };

  // Load saved videos
  const loadSavedVideos = useCallback(async () => {
    setSavedLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(savedPage + 1),
        per_page: String(savedPerPage),
      });
      if (savedSearch) params.append('search', savedSearch);
      if (savedCategory) params.append('category', savedCategory);

      const data = await adminFetch(`${apiUrl}/api/admin/youtube/videos/?${params}`);
      setSavedVideos(data.videos);
      setSavedTotal(data.total);
    } catch (err: any) {
      if (!err.message?.includes('Admin authentication')) {
        setError(err.message || 'Failed to load saved videos');
      }
    } finally {
      setSavedLoading(false);
    }
  }, [adminFetch, savedPage, savedPerPage, savedSearch, savedCategory]);

  // Load channel videos
  const loadChannelVideos = useCallback(async (pageToken?: string) => {
    setChannelLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (pageToken) params.append('page_token', pageToken);

      const data = await adminFetch(`${apiUrl}/api/admin/youtube/channel-videos/?${params}`);
      setConfigured(data.configured);

      if (data.configured) {
        if (pageToken) {
          setChannelVideos(prev => [...prev, ...data.videos]);
        } else {
          setChannelVideos(data.videos);
        }
        setChannelNextPage(data.next_page_token || null);
      }
    } catch (err: any) {
      if (!err.message?.includes('Admin authentication')) {
        setError(err.message || 'Failed to load channel videos');
      }
    } finally {
      setChannelLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => {
    if (tab === 0) {
      loadTooltips();
    } else if (tab === 1) {
      loadSavedVideos();
    } else if (tab === 2 && channelVideos.length === 0) {
      loadChannelVideos();
    }
  }, [tab, loadTooltips, loadSavedVideos, loadChannelVideos, channelVideos.length]);

  // Add video
  const handleAddVideo = async () => {
    if (!addingVideo) return;
    setAddLoading(true);
    setError(null);
    try {
      await adminFetch(`${apiUrl}/api/admin/youtube/videos/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          youtube_video_id: addingVideo.video_id,
          title: addingVideo.title,
          description: addingVideo.description,
          thumbnail_url: addingVideo.thumbnail_url,
          thumbnail_medium_url: addingVideo.thumbnail_medium_url,
          thumbnail_high_url: addingVideo.thumbnail_high_url,
          channel_title: addingVideo.channel_title,
          published_at: addingVideo.published_at,
          duration: addingVideo.duration,
          view_count: addingVideo.view_count,
          like_count: addingVideo.like_count,
          ...addForm,
        }),
      });
      setSuccessMessage('Video saved successfully');
      setAddDialogOpen(false);
      setAddingVideo(null);
      // Update the channel list to reflect the saved state
      setChannelVideos(prev => prev.map(v =>
        v.video_id === addingVideo.video_id ? { ...v, is_saved: true } : v
      ));
      // Refresh saved tab data
      loadSavedVideos();
    } catch (err: any) {
      setError(err.message || 'Failed to save video');
    } finally {
      setAddLoading(false);
    }
  };

  // Edit video
  const handleEditVideo = async () => {
    if (!editingVideo) return;
    setEditLoading(true);
    setError(null);
    try {
      await adminFetch(`${apiUrl}/api/admin/youtube/videos/${editingVideo.id}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      setSuccessMessage('Video updated successfully');
      setEditDialogOpen(false);
      setEditingVideo(null);
      loadSavedVideos();
    } catch (err: any) {
      setError(err.message || 'Failed to update video');
    } finally {
      setEditLoading(false);
    }
  };

  // Delete video
  const handleDeleteVideo = async () => {
    if (!deletingVideo) return;
    setDeleteLoading(true);
    setError(null);
    try {
      await adminFetch(`${apiUrl}/api/admin/youtube/videos/${deletingVideo.id}/`, {
        method: 'DELETE',
      });
      setSuccessMessage('Video removed');
      setDeleteDialogOpen(false);
      setDeletingVideo(null);
      loadSavedVideos();
      // Update channel list if loaded
      setChannelVideos(prev => prev.map(v =>
        v.video_id === deletingVideo.youtube_video_id ? { ...v, is_saved: false } : v
      ));
    } catch (err: any) {
      setError(err.message || 'Failed to delete video');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Sync metadata
  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const data = await adminFetch(`${apiUrl}/api/admin/youtube/videos/sync/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      setSuccessMessage(data.message);
      loadSavedVideos();
    } catch (err: any) {
      setError(err.message || 'Failed to sync videos');
    } finally {
      setSyncing(false);
    }
  };

  // Toggle active
  const handleToggleActive = async (video: YouTubeVideo) => {
    try {
      await adminFetch(`${apiUrl}/api/admin/youtube/videos/${video.id}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !video.active }),
      });
      loadSavedVideos();
    } catch (err: any) {
      setError(err.message || 'Failed to update video');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row" sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/admin')} aria-label="back to admin">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Tutorials & Videos</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="App Tutorial" />
          <Tab label="Saved Videos" />
          <Tab label="Channel Videos" />
        </Tabs>
      </Paper>

      {/* Tab 0: App Tutorial */}
      {tab === 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              select
              label="Section"
              value={selectedSection}
              onChange={(e) => { setSelectedSection(e.target.value); setTooltipEdits({}); }}
              sx={{ minWidth: 250 }}
            >
              {Object.entries(sectionLabels).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={tooltipResetting ? <CircularProgress size={18} /> : <RestoreIcon />}
              onClick={handleResetSection}
              disabled={!selectedSection || tooltipResetting}
            >
              Reset to Defaults
            </Button>
          </Box>

          {tooltipLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : !selectedSection || !tooltipSections[selectedSection] ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">Select a section to view and edit its tooltip steps.</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 60 }}>Step</TableCell>
                    <TableCell sx={{ width: 80 }}>Enabled</TableCell>
                    <TableCell sx={{ width: '30%' }}>Title</TableCell>
                    <TableCell>Content</TableCell>
                    <TableCell sx={{ width: 100 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tooltipSections[selectedSection].map((tooltip) => {
                    const edits = tooltipEdits[tooltip.id] || {};
                    const hasEdits = Object.keys(edits).length > 0;
                    const currentTitle = edits.title ?? tooltip.title;
                    const currentContent = edits.content ?? tooltip.content;

                    return (
                      <TableRow key={tooltip.id}>
                        <TableCell>
                          <Chip label={tooltip.step_index + 1} size="small" />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={tooltip.enabled}
                            onChange={() => handleToggleTooltipEnabled(tooltip)}
                            size="small"
                            disabled={tooltipSaving === tooltip.id}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={currentTitle}
                            onChange={(e) => setTooltipEdits(prev => ({
                              ...prev,
                              [tooltip.id]: { ...prev[tooltip.id], title: e.target.value },
                            }))}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                            value={currentContent}
                            onChange={(e) => setTooltipEdits(prev => ({
                              ...prev,
                              [tooltip.id]: { ...prev[tooltip.id], content: e.target.value },
                            }))}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Save changes">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSaveTooltip(tooltip)}
                                disabled={!hasEdits || tooltipSaving === tooltip.id}
                              >
                                {tooltipSaving === tooltip.id ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Paper sx={{ p: 2, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Edit the title and content for each tooltip step. Use the enabled toggle to show or hide individual steps.
              Changes are saved per-step. Use "Reset to Defaults" to restore the original text for the selected section.
            </Typography>
          </Paper>
        </>
      )}

      {/* Tab 1: Saved Videos */}
      {tab === 1 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search videos..."
              value={savedSearch}
              onChange={(e) => { setSavedSearch(e.target.value); setSavedPage(0); }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
              }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              size="small"
              select
              label="Category"
              value={savedCategory}
              onChange={(e) => { setSavedCategory(e.target.value); setSavedPage(0); }}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(VIDEO_CATEGORY_LABELS).map(([key, label]) => (
                <MenuItem key={key} value={key}>{label}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={18} /> : <SyncIcon />}
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Metadata'}
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Video</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="center">Order</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="center">Featured</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {savedLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : savedVideos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No saved videos. Go to the Channel Videos tab to add some.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  savedVideos.map((video) => (
                    <TableRow key={video.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 80, height: 45, borderRadius: 1, overflow: 'hidden',
                              flexShrink: 0, cursor: 'pointer', position: 'relative',
                              '&:hover .play-overlay': { opacity: 1 },
                            }}
                            onClick={() => {
                              setPreviewVideoId(video.youtube_video_id);
                              setPreviewTitle(video.title);
                              setPreviewDialogOpen(true);
                            }}
                          >
                            <img
                              src={video.thumbnail_url || video.thumbnail_medium_url}
                              alt={video.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <Box
                              className="play-overlay"
                              sx={{
                                position: 'absolute', inset: 0,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.4)', opacity: 0, transition: 'opacity 0.2s',
                              }}
                            >
                              <PlayArrowIcon sx={{ color: 'white', fontSize: 28 }} />
                            </Box>
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>{video.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDuration(video.duration)}
                              {video.view_count > 0 && ` · ${formatCount(video.view_count)} views`}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={VIDEO_CATEGORY_LABELS[video.category]}
                          color={CATEGORY_COLORS[video.category]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">{video.display_order}</TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={video.active}
                          onChange={() => handleToggleActive(video)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {video.featured && <CheckCircleIcon color="success" fontSize="small" />}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setPreviewVideoId(video.youtube_video_id);
                              setPreviewTitle(video.title);
                              setPreviewDialogOpen(true);
                            }}
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingVideo(video);
                              setEditForm({
                                category: video.category,
                                display_order: video.display_order,
                                active: video.active,
                                featured: video.featured,
                                admin_notes: video.admin_notes,
                              });
                              setEditDialogOpen(true);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeletingVideo(video);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={savedTotal}
              page={savedPage}
              onPageChange={(_, p) => setSavedPage(p)}
              rowsPerPage={savedPerPage}
              onRowsPerPageChange={(e) => { setSavedPerPage(parseInt(e.target.value, 10)); setSavedPage(0); }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </TableContainer>
        </>
      )}

      {/* Tab 2: Channel Videos */}
      {tab === 2 && (
        <>
          {configured === false && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              YouTube API is not configured. Add YOUTUBE_API_KEY and YOUTUBE_CHANNEL_ID to your environment variables.
            </Alert>
          )}

          {channelLoading && channelVideos.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {channelVideos.map((video) => (
                  <Paper
                    key={video.video_id}
                    sx={{
                      width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.33% - 11px)' },
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative', cursor: 'pointer',
                        '&:hover .play-overlay': { opacity: 1 },
                      }}
                      onClick={() => {
                        setPreviewVideoId(video.video_id);
                        setPreviewTitle(video.title);
                        setPreviewDialogOpen(true);
                      }}
                    >
                      <img
                        src={video.thumbnail_medium_url || video.thumbnail_url}
                        alt={video.title}
                        style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
                      />
                      <Box
                        className="play-overlay"
                        sx={{
                          position: 'absolute', inset: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: 'rgba(0,0,0,0.4)', opacity: 0, transition: 'opacity 0.2s',
                        }}
                      >
                        <PlayArrowIcon sx={{ color: 'white', fontSize: 48 }} />
                      </Box>
                      {video.duration && (
                        <Chip
                          label={formatDuration(video.duration)}
                          size="small"
                          sx={{
                            position: 'absolute', bottom: 4, right: 4,
                            backgroundColor: 'rgba(0,0,0,0.8)', color: 'white',
                            fontSize: '0.7rem', height: 20,
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{
                        fontWeight: 500,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        mb: 0.5,
                      }}>
                        {video.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        {video.view_count > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <VisibilityIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">{formatCount(video.view_count)}</Typography>
                          </Box>
                        )}
                        {video.like_count > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <ThumbUpIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">{formatCount(video.like_count)}</Typography>
                          </Box>
                        )}
                        {video.published_at && (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(video.published_at).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                      {video.is_saved ? (
                        <Chip label="Saved" color="success" size="small" icon={<CheckCircleIcon />} />
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            setAddingVideo(video);
                            setAddForm({ category: 'general', display_order: 0, active: true, featured: false, admin_notes: '' });
                            setAddDialogOpen(true);
                          }}
                        >
                          Add
                        </Button>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>

              {channelNextPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => loadChannelVideos(channelNextPage)}
                    disabled={channelLoading}
                    startIcon={channelLoading ? <CircularProgress size={18} /> : <YouTubeIcon />}
                  >
                    {channelLoading ? 'Loading...' : 'Load More'}
                  </Button>
                </Box>
              )}

              {!channelLoading && channelVideos.length === 0 && configured && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No videos found on the channel.</Typography>
                </Paper>
              )}
            </>
          )}
        </>
      )}

      {/* Add Video Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Video</DialogTitle>
        <DialogContent>
          {addingVideo && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <img
                src={addingVideo.thumbnail_url}
                alt={addingVideo.title}
                style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 4 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{addingVideo.title}</Typography>
            </Box>
          )}
          <TextField
            select
            label="Category"
            value={addForm.category}
            onChange={(e) => setAddForm({ ...addForm, category: e.target.value as VideoCategory })}
            fullWidth
            sx={{ mb: 2, mt: 1 }}
          >
            {Object.entries(VIDEO_CATEGORY_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Display Order"
            type="number"
            value={addForm.display_order}
            onChange={(e) => setAddForm({ ...addForm, display_order: parseInt(e.target.value, 10) || 0 })}
            fullWidth
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Switch checked={addForm.active} onChange={(e) => setAddForm({ ...addForm, active: e.target.checked })} />}
            label="Active"
          />
          <FormControlLabel
            control={<Switch checked={addForm.featured} onChange={(e) => setAddForm({ ...addForm, featured: e.target.checked })} />}
            label="Featured"
          />
          <TextField
            label="Admin Notes"
            value={addForm.admin_notes}
            onChange={(e) => setAddForm({ ...addForm, admin_notes: e.target.value })}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddVideo}
            disabled={addLoading}
            startIcon={addLoading ? <CircularProgress size={18} /> : <AddIcon />}
          >
            {addLoading ? 'Saving...' : 'Save Video'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Video Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Video</DialogTitle>
        <DialogContent>
          {editingVideo && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <img
                src={editingVideo.thumbnail_url || editingVideo.thumbnail_medium_url}
                alt={editingVideo.title}
                style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 4 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{editingVideo.title}</Typography>
            </Box>
          )}
          <TextField
            select
            label="Category"
            value={editForm.category}
            onChange={(e) => setEditForm({ ...editForm, category: e.target.value as VideoCategory })}
            fullWidth
            sx={{ mb: 2, mt: 1 }}
          >
            {Object.entries(VIDEO_CATEGORY_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Display Order"
            type="number"
            value={editForm.display_order}
            onChange={(e) => setEditForm({ ...editForm, display_order: parseInt(e.target.value, 10) || 0 })}
            fullWidth
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={<Switch checked={editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} />}
            label="Active"
          />
          <FormControlLabel
            control={<Switch checked={editForm.featured} onChange={(e) => setEditForm({ ...editForm, featured: e.target.checked })} />}
            label="Featured"
          />
          <TextField
            label="Admin Notes"
            value={editForm.admin_notes}
            onChange={(e) => setEditForm({ ...editForm, admin_notes: e.target.value })}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditVideo}
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={18} /> : <EditIcon />}
          >
            {editLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Video</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deletingVideo?.title}</strong> from your saved videos? This does not delete it from YouTube.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteVideo}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={18} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{previewTitle}</DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
            {previewVideoId && (
              <iframe
                src={`https://www.youtube.com/embed/${previewVideoId}`}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={previewTitle}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminTutorials;
